import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  AttachMoney,
  TrendingUp,
  AccountBalance,
  EmojiEvents,
  CheckCircle,
  HourglassEmpty,
} from "@mui/icons-material";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const Commission = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("current");
  const [commissionData, setCommissionData] = useState({
    totalEarned: 0,
    pendingCommission: 0,
    paidCommission: 0,
    currentMonth: 0,
    commissionRate: 15,
    goal: 5000,
    transactions: [],
  });

  useEffect(() => {
    fetchCommissionData();
  }, [period]);

  const numberLocale = (() => {
    const l = String(i18n.language || "").toLowerCase();
    if (l.startsWith("pt")) return "pt-BR";
    if (l.startsWith("en")) return "en-US";
    if (l.startsWith("fr")) return "fr-FR";
    if (l.startsWith("it")) return "it-IT";
    return "es-ES";
  })();

  const formatMoney = (value, options = undefined) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return `$${(0).toLocaleString(numberLocale, options)}`;
    return `$${n.toLocaleString(numberLocale, options)}`;
  };

  const periodLabel = (() => {
    if (period === "current") return i18n.t("commission.period.current");
    if (period === "last") return i18n.t("commission.period.last");
    if (period === "quarter") return i18n.t("commission.period.quarter");
    if (period === "year") return i18n.t("commission.period.year");
    return i18n.t("commission.period.selected");
  })();

  const fetchCommissionData = async () => {
    try {
      setLoading(true);

      // Fetch real commission data from API
      const [commissionsRes, salesRes] = await Promise.all([
        api.get(`/commissions?period=${period}`).catch(() => ({ data: {} })),
        api.get('/dashboard/sales-summary').catch(() => ({ data: {} }))
      ]);

      const commissions = commissionsRes.data || {};
      const sales = salesRes.data || {};

      // Calculate commission data from sales if commissions endpoint not available
      const transactions = commissions.transactions || [];
      const totalEarned = commissions.totalEarned || transactions.reduce((sum, t) => sum + (t.commission || 0), 0);
      const paidCommission = transactions.filter(t => t.status === 'pagado' || t.status === 'paid')
        .reduce((sum, t) => sum + (t.commission || 0), 0);
      const pendingCommission = transactions.filter(t => t.status === 'pendiente' || t.status === 'pending')
        .reduce((sum, t) => sum + (t.commission || 0), 0);

      // Calculate current month from sales if available
      const currentMonth = commissions.currentMonth || (sales.totalSales ? sales.totalSales * 0.15 : pendingCommission);

      setCommissionData({
        totalEarned: commissions.totalEarned || totalEarned,
        pendingCommission: commissions.pendingCommission || pendingCommission,
        paidCommission: commissions.paidCommission || paidCommission,
        currentMonth: Math.round(currentMonth),
        commissionRate: commissions.commissionRate || 15,
        goal: commissions.goal || 5000,
        transactions: transactions.map(t => ({
          id: t.id,
          client: t.client || t.client_name || "",
          plan: t.plan || t.product || "",
          amount: t.amount || t.sale_amount || 0,
          commission: t.commission || 0,
          status: t.status === 'paid' || t.status === 'pagado'
            ? 'paid'
            : t.status === 'pending' || t.status === 'pendiente'
            ? 'pending'
            : t.status,
          date: t.date || t.created_at || new Date().toISOString().split('T')[0]
        }))
      });

    } catch (error) {
      console.error("Error fetching commission data:", error);
      toast.error(i18n.t("commission.errors.load"));
    } finally {
      setLoading(false);
    }
  };

  const goalProgress =
    (commissionData.currentMonth / commissionData.goal) * 100;

  const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        borderLeft: `4px solid ${color}`,
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography color="textSecondary" variant="body2" fontWeight={600}>
            {title}
          </Typography>
          <Icon sx={{ color, fontSize: 28 }} />
        </Box>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Paper
      sx={{
        p: 3,
        minHeight: "calc(100vh - 100px)",
        background: "linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)",
      }}
      elevation={0}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {i18n.t("commission.title")}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {i18n.t("commission.subtitle")}
          </Typography>
        </Box>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{i18n.t("commission.period.label")}</InputLabel>
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            label={i18n.t("commission.period.label")}
          >
            <MenuItem value="current">{i18n.t("commission.period.current")}</MenuItem>
            <MenuItem value="last">{i18n.t("commission.period.last")}</MenuItem>
            <MenuItem value="quarter">{i18n.t("commission.period.quarter")}</MenuItem>
            <MenuItem value="year">{i18n.t("commission.period.year")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <LinearProgress sx={{ mb: 3 }} />
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title={i18n.t("commission.metrics.totalEarned")}
                value={formatMoney(commissionData.totalEarned)}
                icon={AttachMoney}
                color="#10b981"
                subtitle={i18n.t("commission.metrics.cumulative")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title={i18n.t("commission.metrics.currentMonth")}
                value={formatMoney(commissionData.currentMonth)}
                icon={TrendingUp}
                color="#6366f1"
                subtitle={i18n.t("commission.metrics.rateSubtitle", {
                  rate: commissionData.commissionRate,
                })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title={i18n.t("commission.metrics.pendingPayout")}
                value={formatMoney(commissionData.pendingCommission)}
                icon={HourglassEmpty}
                color="#f59e0b"
                subtitle={i18n.t("commission.metrics.toProcess")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title={i18n.t("commission.metrics.paid")}
                value={formatMoney(commissionData.paidCommission)}
                icon={AccountBalance}
                color="#22c55e"
                subtitle={i18n.t("commission.metrics.processed")}
              />
            </Grid>
          </Grid>

          {/* Goal Progress */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <EmojiEvents sx={{ mr: 1, color: "#f59e0b" }} />
                <Typography variant="h6" fontWeight={600}>
                  {i18n.t("commission.goal.title", { period: periodLabel })}
                </Typography>
              </Box>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t("commission.goal.progress", {
                          percent: goalProgress.toFixed(1),
                        })}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatMoney(commissionData.currentMonth)} / {formatMoney(commissionData.goal)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(goalProgress, 100)}
                      sx={{
                        height: 12,
                        borderRadius: 6,
                        backgroundColor: "#e5e7eb",
                        "& .MuiLinearProgress-bar": {
                          background:
                            "linear-gradient(90deg, #f59e0b 0%, #fb923c 100%)",
                          borderRadius: 6,
                        },
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {goalProgress >= 100
                      ? i18n.t("commission.goal.reached")
                      : i18n.t("commission.goal.remaining", {
                          amount: formatMoney(commissionData.goal - commissionData.currentMonth),
                        })}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {i18n.t("commission.goal.rateLabel")}
                    </Typography>
                    <Typography variant="h3" fontWeight={700} color="primary">
                      {commissionData.commissionRate}%
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Transactions Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {i18n.t("commission.table.title")}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableRow>
                      <TableCell>
                        <strong>{i18n.t("commission.table.headers.client")}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{i18n.t("commission.table.headers.plan")}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{i18n.t("commission.table.headers.saleValue")}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{i18n.t("commission.table.headers.commission")}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{i18n.t("commission.table.headers.status")}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{i18n.t("commission.table.headers.date")}</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {commissionData.transactions.map((transaction) => (
                      <TableRow key={transaction.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                            {transaction.client || i18n.t("commission.defaults.client")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                            label={transaction.plan || i18n.t("commission.defaults.plan")}
                          size="small"
                          sx={{
                            backgroundColor: "#6366f1",
                            color: "white",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {formatMoney(transaction.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="primary"
                          >
                            {formatMoney(transaction.commission, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {transaction.status === "paid" ? (
                            <Chip
                              icon={<CheckCircle />}
                              label={i18n.t("commission.status.paid")}
                              size="small"
                              sx={{
                                backgroundColor: "#10b981",
                                color: "white",
                                fontWeight: 600,
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<HourglassEmpty />}
                              label={i18n.t("commission.status.pending")}
                              size="small"
                              sx={{
                                backgroundColor: "#f59e0b",
                                color: "white",
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {transaction.date}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 3, p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("commission.summary.totalPaid")}:
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="#10b981">
                      {formatMoney(
                        commissionData.transactions
                          .filter((t) => t.status === "paid")
                          .reduce((sum, t) => sum + t.commission, 0),
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("commission.summary.totalPending")}:
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="#f59e0b">
                      {formatMoney(
                        commissionData.transactions
                          .filter((t) => t.status === "pending")
                          .reduce((sum, t) => sum + t.commission, 0),
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Paper>
  );
};

export default Commission;
